// Same-name upload: what it destroys, and how to describe it.
//
// The documents backend keys a document on (account_id, filename, project_id).
// On a hit it does not create a second row and does not reject: it replaces the
// existing row and overwrites the same S3 object, then returns a plain HTTP 200.
// Nothing ever compares the bytes, so a byte-identical re-upload is treated as
// changed content and re-runs a paid extraction pass.
//
// The order of an upload is what makes this subtle:
//
//   1. POST /documents/get-signed-url   → presigned PUT URL
//   2. PUT the bytes to S3              ← the previous file dies HERE
//   3. POST /documents/create           → the row is replaced
//
// Step 3 is where the server's own filename lookup lives, but by then the object
// is already overwritten — a conflict reported there would announce a loss that
// already happened. So step 1 carries the warning instead, and these helpers are
// how the client reads and renders it.

/**
 * The filename the server stores and keys on.
 *
 * Every upload slash-replaces the picked name before sending it. Deriving that
 * in more than one place is how a check ends up testing a name the upload never
 * sends, so this is the single definition.
 */
export const uploadFilename = (name?: string | null): string =>
  String(name ?? '').replace(/\//g, '-');

/**
 * What an upload would destroy, as reported by /documents/get-signed-url.
 *
 * Only ever describes a document the caller's own account owns — the endpoint
 * deliberately withholds the global/partner static rows it also matches, which
 * belong to other accounts.
 */
export interface DocumentOverwrite {
  id?: number | string;
  filename?: string | null;
  size?: number | null;
  updated_at?: string | null;
}

const sizeLabel = (bytes?: number | null): string => {
  if (typeof bytes !== 'number' || !Number.isFinite(bytes) || bytes <= 0) return '';
  const units = ['B', 'KB', 'MB', 'GB'];
  let n = bytes;
  let unit = 0;
  while (n >= 1024 && unit < units.length - 1) { n /= 1024; unit += 1; }
  return `${n < 10 && unit > 0 ? n.toFixed(1) : Math.round(n)} ${units[unit]}`;
};

const whenLabel = (iso?: string | null): string => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return '';
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days <= 0) return 'today';
  if (days === 1) return 'yesterday';
  if (days < 30) return `${days} days ago`;
  if (days < 365) return `${Math.floor(days / 30)} months ago`;
  return `${Math.floor(days / 365)} years ago`;
};

/**
 * One line describing the document about to be replaced, for a confirm dialog.
 *
 * Size and age are what make the question answerable — a filename alone doesn't
 * tell someone whether the copy already on the plan is the one worth keeping.
 */
export function describeOverwrite(overwrites: DocumentOverwrite): string {
  const name = String(overwrites?.filename || 'This file').trim();
  const parts = [sizeLabel(overwrites?.size), whenLabel(overwrites?.updated_at)].filter(Boolean);
  const detail = parts.length ? ` (${parts.join(', added ')})` : '';
  return `"${name}"${detail} is already on this plan.\n\n`
    + 'Replacing overwrites it and re-runs processing. This cannot be undone.';
}
